<script setup lang="ts">
import FinestInputText from '@/components/FinestInputText.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectType = App.Enums.EstateObject.EstateObjectType

defineProps<{
  typeOptions: { value: EstateObjectType; label: string }[]
  shownCount: number
  totalCount: number
}>()

const search = defineModel<string | null>('search', { default: null })
const type = defineModel<EstateObjectType | null>('type', { default: null })
const groupByOwner = defineModel<boolean>('groupByOwner', { default: false })

const { t } = useI18n({ useScope: 'local' })

const groupOptions = computed(() => [
  { value: false, label: t('groupNone') },
  { value: true, label: t('groupByOwner') },
])
</script>

<template>
  <div class="mb-4 flex flex-wrap items-center gap-3 px-5">
    <FinestInputText
      v-model="search"
      icon="pi pi-search"
      :placeholder="t('searchPlaceholder')"
      :aria-label="t('search')"
      class="min-w-55 flex-1 sm:max-w-90" />

    <FinestSelect
      v-model="type"
      :options="typeOptions"
      :placeholder="t('allTypes')"
      :aria-label="t('type')"
      class="min-w-39.5" />

    <FinestSelect
      :model-value="groupByOwner"
      :options="groupOptions"
      :aria-label="t('grouping')"
      class="min-w-39.5"
      @update:model-value="(value) => (groupByOwner = Boolean(value))" />

    <span class="text-ink-3 ml-auto whitespace-nowrap">
      {{ t('resultCount', { shown: shownCount, total: totalCount }) }}
    </span>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "search": "Suche",
    "searchPlaceholder": "Adresse oder Ort suchen …",
    "type": "Objektart",
    "allTypes": "Alle Objektarten",
    "grouping": "Gruppierung",
    "groupNone": "Gruppierung: keine",
    "groupByOwner": "nach Eigentümer",
    "resultCount": "{shown} von {total} Objekten"
  },
  "en": {
    "search": "Search",
    "searchPlaceholder": "Search address or city …",
    "type": "Property type",
    "allTypes": "All property types",
    "grouping": "Grouping",
    "groupNone": "Grouping: none",
    "groupByOwner": "by owner",
    "resultCount": "{shown} of {total} properties"
  }
}
</i18n>
